import React from 'react';
import {
  RendererFactory, SurveyModel,
} from 'survey-core';
import { ReactQuestionFactory, SurveyQuestionSignaturePad } from 'survey-react-ui';
import { Box } from '@mui/material';
import { SignerInvite } from './signer-invite';
import {
  Submission, SurveySettings, SIGNED_SUBMISSION_STATUSES,
} from '../../../types';
import { requestSigner } from '../../../api/signatures/request-signer';
import { deleteSigner } from '../../../api/signatures/delete-signer';

const SIGNATURE_PAD_QUESTION_TYPE = 'signaturepad';

interface RegisterSignaturePadWithInviteQuestionProps {
  settings: SurveySettings;
  submission?: Submission;
  isSigner?: boolean;
}

export const registerSignaturePadWithInviteQuestion = ({
  settings,
  submission,
  isSigner = false,
}: RegisterSignaturePadWithInviteQuestionProps) => {
  if (!submission || isSigner) return;
  if (SIGNED_SUBMISSION_STATUSES.has(submission.status)) return;

  class SignaturePadWithInviteQuestion extends SurveyQuestionSignaturePad {
    constructor (props: any) {
      super(props);
      this.handleInviteSigner = this.handleInviteSigner.bind(this);
      this.handleCancelInvite = this.handleCancelInvite.bind(this);
    }

    get surveyModel () {
      return this.question.getSurvey() as SurveyModel;
    }

    async handleInviteSigner (email: string) {
      if (!submission) {
        throw new Error('Submission does not exist');
      }

      // Validate current page format only (not required fields) before sending responses
      const currentPage = this.surveyModel.currentPage;
      if (currentPage) {
        // Validate only questions that have values (format validation, not required)
        const questions = currentPage.questions;
        for (let i = 0; i < questions.length; i++) {
          const question = questions[i];
          // Skip empty questions to avoid required validation
          if (!question.isEmpty()) {
            question.validate();
            if (question.hasErrors()) {
              // Format validation failed, don't proceed with invite
              throw new Error('Invite not sent - Please fix or clear the invalid fields');
            }
          }
        }
      }

      // Collect current responses from the survey before inviting signer
      const currentResponses = this.surveyModel.data;

      await requestSigner(email, settings.urlSlug, submission.publicId, currentResponses);

      // Set the survey to readOnly, so only the signer can edit unless the invite is canceled
      this.surveyModel.readOnly = true;

      // Update submission.signer so other SignerInvite components also display the signer email
      submission.signer = {
        email,
        isInitialUser: true,
        isVerified: false,
      };

      return true;
    }

    async handleCancelInvite () {
      if (!submission) {
        throw new Error('Submission does not exist');
      }

      await deleteSigner(settings.urlSlug, submission.publicId);

      // Set the survey back to edit mode
      this.surveyModel.readOnly = false;

      submission.signer = undefined;

      return true;
    }

    renderElement () {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {submission && (
            <SignerInvite
              isSignerVerified={submission?.signer?.isVerified}
              signerEmail={submission.signer?.email}
              userEmail={submission.details?.email}
              onCancel={this.handleCancelInvite}
              onInvite={this.handleInviteSigner}
            />
          )}
          {super.renderElement()}
        </Box>
      );
    }
  }

  ReactQuestionFactory.Instance.registerQuestion(
    'sv-signaturepad-with-invite',
    function (props) {
      return React.createElement(SignaturePadWithInviteQuestion, props);
    },
  );

  RendererFactory.Instance.registerRenderer(
    SIGNATURE_PAD_QUESTION_TYPE,
    'signaturepad-with-invite',
    'sv-signaturepad-with-invite',
    true,
  );
};
